// src/cockpit4/components/ProjectDashboard.tsx
// ============================================
// P0-功能1：项目全景看板（项目中心）
//
// 功能：
//   1. 看板视图（按状态分列：进行中/延期/已完成/风险/暂停）
//   2. 列表视图切换
//   3. 项目卡片：名称、进度、状态、负责人、截止日、健康度
//   4. 项目健康度评分（4级：健康/关注/风险/危险，基于进度偏差计算）
//   5. 筛选和搜索功能（按名称、状态、负责人）
//   6. 点击项目卡片展开详情
//   7. 新增项目（弹窗表单：名称、描述、负责人、开始/截止日期）
//
// 数据来源：
//   - Runtime Catalog（RuntimeManager.query 各 Runtime 的 catalog）
//   - localStorage 持久化的自定义项目（xeaos_projects_custom）
// ============================================

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FolderKanban,
  Search,
  Plus,
  X,
  RefreshCw,
  Loader2,
  User,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Pause,
  Clock,
  Activity,
  Shield,
  TrendingUp,
  ExternalLink,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { ProductCatalogInfo } from "../../runtime/ProductRuntimes/types";

// ============================================
// 类型定义
// ============================================

/** 项目状态（看板分列依据） */
export type ProjectStatus =
  | "in_progress" // 进行中
  | "overdue" // 延期
  | "completed" // 已完成
  | "at_risk" // 风险
  | "paused"; // 暂停

/** 项目健康度（4级） */
export type HealthLevel = "healthy" | "watch" | "risk" | "danger";

/** 项目模型（统一 Runtime 项目与本地自建项目） */
export interface Project {
  id: string;
  name: string;
  description: string;
  owner: string;
  /** 开始日期 YYYY-MM-DD（本地项目必填，Runtime 项目可选） */
  startDate?: string;
  /** 截止日期 YYYY-MM-DD（从里程碑推导或用户填写） */
  dueDate?: string;
  status: ProjectStatus;
  /** 实际进度 0-100 */
  progress: number;
  health: HealthLevel;
  /** 数据来源：Runtime Catalog 或本地自建 */
  source: "runtime" | "local";
  runtimeName?: string;
  category?: string;
  capabilities?: string[];
  launchUrl?: string;
  milestones?: Array<{
    name: string;
    dueDate: string;
    status: "completed" | "in_progress" | "pending";
  }>;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// 常量配置
// ============================================

const STORAGE_KEY = "xeaos_projects_custom";

/** 要查询的 Runtime 列表（复用 ProjectsWorkspace 的数据获取方式） */
const PRODUCT_RUNTIMES: RuntimeName[] = [
  RuntimeName.Hotel,
  RuntimeName.GoldFactory,
  RuntimeName.AIDestiny,
  RuntimeName.MarketingOS,
];

const STATUS_META: Record<
  ProjectStatus,
  { label: string; color: string; icon: typeof Clock }
> = {
  in_progress: { label: "进行中", color: "#3B82F6", icon: Activity },
  overdue: { label: "延期", color: "#EF4444", icon: AlertTriangle },
  completed: { label: "已完成", color: "#10B981", icon: CheckCircle2 },
  at_risk: { label: "风险", color: "#F59E0B", icon: Shield },
  paused: { label: "暂停", color: "#9CA3AF", icon: Pause },
};

/** 看板列顺序 */
const BOARD_COLUMNS: ProjectStatus[] = [
  "in_progress",
  "overdue",
  "at_risk",
  "completed",
  "paused",
];

const HEALTH_META: Record<
  HealthLevel,
  { label: string; color: string }
> = {
  healthy: { label: "健康", color: "#10B981" },
  watch: { label: "关注", color: "#F59E0B" },
  risk: { label: "风险", color: "#F97316" },
  danger: { label: "危险", color: "#EF4444" },
};

// ============================================
// localStorage 持久化
// ============================================

/** 从 localStorage 加载自建项目 */
export function loadCustomProjects(): Project[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as Project[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

/** 保存自建项目到 localStorage */
function saveCustomProjects(projects: Project[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
  } catch {
    // localStorage 不可用时静默降级
  }
}

// ============================================
// 工具函数
// ============================================

/** 根据时间线计算计划进度（0-100），无日期返回 null */
export function calculatePlannedProgress(
  startDate?: string,
  dueDate?: string
): number | null {
  if (!startDate || !dueDate) return null;
  const start = new Date(startDate).getTime();
  const due = new Date(dueDate).getTime();
  if (isNaN(start) || isNaN(due) || due <= start) return null;
  const now = Date.now();
  if (now <= start) return 0;
  if (now >= due) return 100;
  return Math.round(((now - start) / (due - start)) * 100);
}

/** 基于进度偏差计算健康度 */
export function calculateHealth(
  actualProgress: number,
  plannedProgress: number | null
): HealthLevel {
  if (plannedProgress === null) {
    // 无日期信息时基于实际进度粗判
    if (actualProgress >= 80) return "healthy";
    if (actualProgress >= 50) return "watch";
    if (actualProgress >= 20) return "risk";
    return "danger";
  }
  const deviation = plannedProgress - actualProgress;
  if (deviation <= 5) return "healthy";
  if (deviation <= 15) return "watch";
  if (deviation <= 30) return "risk";
  return "danger";
}

/** 综合推导项目状态 */
function deriveStatus(
  progress: number,
  dueDate: string | undefined,
  health: HealthLevel,
  runtimePaused: boolean
): ProjectStatus {
  if (runtimePaused) return "paused";
  if (progress >= 100) return "completed";
  if (dueDate) {
    const due = new Date(dueDate).getTime();
    if (!isNaN(due) && Date.now() > due && progress < 100) return "overdue";
  }
  if (health === "danger" || health === "risk") return "at_risk";
  return "in_progress";
}

/** 格式化日期为 YYYY-MM-DD */
function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

// ============================================
// 主组件
// ============================================

export default function ProjectDashboard() {
  const [view, setView] = useState<"board" | "list">("board");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | "all">("all");
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // ============================================
  // 加载项目数据（Runtime Catalog + localStorage）
  // ============================================
  const loadProjects = useCallback(async () => {
    setLoading(true);
    const localProjects = loadCustomProjects();

    // 等待 RuntimeManager 就绪
    let runtimeReady = false;
    for (let i = 0; i < 15; i++) {
      if (RuntimeManager.isInitialized()) {
        runtimeReady = true;
        break;
      }
      await new Promise((r) => setTimeout(r, 200));
    }

    const runtimeProjects: Project[] = [];
    if (runtimeReady) {
      const results = await Promise.allSettled(
        PRODUCT_RUNTIMES.map(async (name) => {
          if (!RuntimeManager.hasRuntime(name)) return null;
          try {
            const resp = await RuntimeManager.query(name, { type: "catalog" });
            if (resp?.success && resp.data) {
              const productResult = resp.data as {
                success: boolean;
                data?: ProductCatalogInfo;
              };
              if (productResult.success && productResult.data) {
                const c = productResult.data;
                return c;
              }
            }
          } catch {
            // 查询失败跳过
          }
          return null;
        })
      );

      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value) {
          const c = result.value;
          // 从里程碑推导截止日
          const dueDate =
            c.milestones.length > 0
              ? c.milestones[c.milestones.length - 1].dueDate
              : undefined;
          const planned = calculatePlannedProgress(undefined, dueDate);
          const health = calculateHealth(c.progress, planned);
          const status = deriveStatus(
            c.progress,
            dueDate,
            health,
            c.runtimeStatus === "paused"
          );
          runtimeProjects.push({
            id: `runtime_${c.productId}`,
            name: c.displayName,
            description: c.description,
            owner: c.owner,
            dueDate,
            status,
            progress: c.progress,
            health,
            source: "runtime",
            runtimeName: c.productId,
            category: c.category,
            capabilities: c.capabilities,
            launchUrl: c.launchUrl,
            milestones: c.milestones,
            createdAt: c.updatedAt || new Date().toISOString(),
            updatedAt: c.updatedAt || new Date().toISOString(),
          });
        }
      });
    }

    setProjects([...runtimeProjects, ...localProjects]);
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  // ============================================
  // 新增本地项目
  // ============================================
  const handleAddProject = useCallback(
    (data: {
      name: string;
      description: string;
      owner: string;
      startDate: string;
      dueDate: string;
    }) => {
      const now = new Date().toISOString();
      const planned = calculatePlannedProgress(data.startDate, data.dueDate);
      const health = calculateHealth(0, planned);
      const status = deriveStatus(0, data.dueDate, health, false);
      const newProject: Project = {
        id: `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name: data.name,
        description: data.description,
        owner: data.owner,
        startDate: data.startDate,
        dueDate: data.dueDate,
        status,
        progress: 0,
        health,
        source: "local",
        createdAt: now,
        updatedAt: now,
      };
      const localProjects = loadCustomProjects();
      const updated = [newProject, ...localProjects];
      saveCustomProjects(updated);
      setProjects((prev) => [newProject, ...prev]);
      setShowAddModal(false);
    },
    []
  );

  // ============================================
  // 筛选与搜索
  // ============================================
  const owners = useMemo(() => {
    const set = new Set<string>();
    projects.forEach((p) => {
      if (p.owner && p.owner !== "—") set.add(p.owner);
    });
    return Array.from(set).sort();
  }, [projects]);

  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      if (search) {
        const q = search.toLowerCase();
        if (
          !p.name.toLowerCase().includes(q) &&
          !p.description.toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (ownerFilter !== "all" && p.owner !== ownerFilter) return false;
      return true;
    });
  }, [projects, search, statusFilter, ownerFilter]);

  // 统计
  const stats = useMemo(() => {
    const byStatus: Record<ProjectStatus, number> = {
      in_progress: 0,
      overdue: 0,
      completed: 0,
      at_risk: 0,
      paused: 0,
    };
    projects.forEach((p) => byStatus[p.status]++);
    return {
      total: projects.length,
      byStatus,
      healthy: projects.filter((p) => p.health === "healthy").length,
      danger: projects.filter((p) => p.health === "danger").length,
    };
  }, [projects]);

  // 按状态分组（看板视图）
  const projectsByStatus = useMemo(() => {
    const map: Record<ProjectStatus, Project[]> = {
      in_progress: [],
      overdue: [],
      completed: [],
      at_risk: [],
      paused: [],
    };
    filteredProjects.forEach((p) => map[p.status].push(p));
    return map;
  }, [filteredProjects]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-purple-500/15 border border-purple-500/30">
            <FolderKanban size={13} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              项目全景看板
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Project Dashboard · {stats.total} 个项目 · 健康 {stats.healthy} ·
              危险 {stats.danger}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 视图切换 */}
          <div className="flex items-center rounded-md border border-white/[0.06] overflow-hidden">
            <button
              onClick={() => setView("board")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                view === "board"
                  ? "bg-purple-500/20 text-purple-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              看板
            </button>
            <button
              onClick={() => setView("list")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                view === "list"
                  ? "bg-purple-500/20 text-purple-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              列表
            </button>
          </div>

          {/* 新增项目 */}
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors"
          >
            <Plus size={11} className="text-purple-400" />
            <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">
              新增项目
            </span>
          </button>

          <button
            onClick={loadProjects}
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={11} className="text-gray-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* 筛选栏 */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-white/[0.04]">
        {/* 搜索 */}
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.02] border border-white/[0.04] flex-1 max-w-[200px]">
          <Search size={10} className="text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索项目名称..."
            className="flex-1 bg-transparent text-[10px] font-mono text-gray-300 placeholder-gray-600 outline-none"
          />
        </div>

        {/* 状态筛选 */}
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as ProjectStatus | "all")
          }
          className="px-2 py-1 rounded-md bg-white/[0.02] border border-white/[0.04] text-[9px] font-mono text-gray-400 outline-none cursor-pointer"
        >
          <option value="all">全部状态</option>
          {(Object.keys(STATUS_META) as ProjectStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_META[s].label}
            </option>
          ))}
        </select>

        {/* 负责人筛选 */}
        <select
          value={ownerFilter}
          onChange={(e) => setOwnerFilter(e.target.value)}
          className="px-2 py-1 rounded-md bg-white/[0.02] border border-white/[0.04] text-[9px] font-mono text-gray-400 outline-none cursor-pointer"
        >
          <option value="all">全部负责人</option>
          {owners.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>

        <span className="ml-auto text-[9px] font-mono text-gray-600">
          {filteredProjects.length}/{projects.length}
        </span>
      </div>

      {/* 主体内容 */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 size={20} className="animate-spin text-purple-400" />
            <span className="ml-2 text-[11px] font-mono text-gray-500">
              加载项目数据...
            </span>
          </div>
        ) : filteredProjects.length === 0 ? (
          <EmptyState onAdd={() => setShowAddModal(true)} />
        ) : view === "board" ? (
          /* 看板视图 */
          <div className="flex gap-3 overflow-x-auto pb-2">
            {BOARD_COLUMNS.map((status) => {
              const meta = STATUS_META[status];
              const SIcon = meta.icon;
              const columnProjects = projectsByStatus[status];
              return (
                <div
                  key={status}
                  className="flex-shrink-0 w-[260px] rounded-lg border border-white/[0.04] bg-white/[0.01]"
                >
                  {/* 列头 */}
                  <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.04]">
                    <div className="flex items-center gap-1.5">
                      <SIcon size={11} style={{ color: meta.color }} />
                      <span
                        className="text-[10px] font-mono uppercase tracking-wider"
                        style={{ color: meta.color }}
                      >
                        {meta.label}
                      </span>
                    </div>
                    <span className="text-[9px] font-mono text-gray-600">
                      {columnProjects.length}
                    </span>
                  </div>
                  {/* 列卡片 */}
                  <div className="p-2 space-y-2 max-h-[480px] overflow-y-auto">
                    {columnProjects.map((p) => (
                      <ProjectCard
                        key={p.id}
                        project={p}
                        expanded={expandedId === p.id}
                        onToggle={() =>
                          setExpandedId(expandedId === p.id ? null : p.id)
                        }
                      />
                    ))}
                    {columnProjects.length === 0 && (
                      <div className="text-center text-[9px] font-mono text-gray-700 py-4">
                        暂无项目
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* 列表视图 */
          <div className="space-y-1.5 max-h-[520px] overflow-y-auto">
            {filteredProjects.map((p) => (
              <ProjectRow
                key={p.id}
                project={p}
                expanded={expandedId === p.id}
                onToggle={() =>
                  setExpandedId(expandedId === p.id ? null : p.id)
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* 新增项目弹窗 */}
      <AnimatePresence>
        {showAddModal && (
          <AddProjectModal
            onSubmit={handleAddProject}
            onClose={() => setShowAddModal(false)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 空状态
// ============================================
function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <FolderKanban size={32} className="text-gray-700 mb-2" />
      <p className="text-[12px] text-gray-500 mb-1">暂无项目数据</p>
      <p className="text-[10px] font-mono text-gray-600 mb-3">
        点击「新增项目」创建第一个项目，或等待 Runtime Catalog 加载
      </p>
      <button
        onClick={onAdd}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors"
      >
        <Plus size={12} className="text-purple-400" />
        <span className="text-[10px] font-mono text-purple-300 uppercase tracking-wider">
          新增项目
        </span>
      </button>
    </div>
  );
}

// ============================================
// 项目卡片（看板视图）
// ============================================
function ProjectCard({
  project,
  expanded,
  onToggle,
}: {
  project: Project;
  expanded: boolean;
  onToggle: () => void;
}) {
  const hMeta = HEALTH_META[project.health];
  const sMeta = STATUS_META[project.status];

  return (
    <div
      className="rounded-md border bg-white/[0.02] overflow-hidden hover:bg-white/[0.03] transition-colors"
      style={{ borderColor: `${sMeta.color}25` }}
    >
      <button onClick={onToggle} className="w-full p-2.5 text-left">
        {/* 标题行 */}
        <div className="flex items-start gap-1.5 mb-1.5">
          <div
            className="flex-shrink-0 w-1 h-1 rounded-full mt-1.5"
            style={{ background: hMeta.color }}
            title={`健康度：${hMeta.label}`}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-gray-200 font-medium leading-snug truncate">
              {project.name}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              {project.source === "runtime" && (
                <span className="px-1 rounded text-[8px] font-mono uppercase bg-cyan-500/15 text-cyan-400">
                  Runtime
                </span>
              )}
              {project.source === "local" && (
                <span className="px-1 rounded text-[8px] font-mono uppercase bg-purple-500/15 text-purple-400">
                  本地
                </span>
              )}
              <span
                className="px-1 rounded text-[8px] font-mono uppercase"
                style={{ background: `${hMeta.color}20`, color: hMeta.color }}
              >
                {hMeta.label}
              </span>
            </div>
          </div>
        </div>

        {/* 进度条 */}
        <div className="mb-1.5">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[8px] font-mono text-gray-600 uppercase">
              进度
            </span>
            <span className="text-[9px] font-mono text-gray-400">
              {project.progress}%
            </span>
          </div>
          <div className="h-0.5 rounded-full bg-white/[0.04] overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${project.progress}%`,
                background: hMeta.color,
              }}
            />
          </div>
        </div>

        {/* 元信息 */}
        <div className="flex items-center justify-between text-[9px] font-mono text-gray-600">
          <span className="flex items-center gap-0.5">
            <User size={8} />
            {project.owner}
          </span>
          <span className="flex items-center gap-0.5">
            <Calendar size={8} />
            {formatDate(project.dueDate)}
          </span>
        </div>
      </button>

      {/* 展开详情 */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-white/[0.03]"
          >
            <div className="p-2.5 space-y-1.5">
              <p className="text-[10px] text-gray-400 leading-snug">
                {project.description}
              </p>

              {/* 健康度详情 */}
              <div className="flex items-center gap-2 text-[9px] font-mono">
                <span className="text-gray-600">健康度：</span>
                <span style={{ color: hMeta.color }}>{hMeta.label}</span>
                {project.startDate && project.dueDate && (
                  <>
                    <span className="text-gray-700">·</span>
                    <span className="text-gray-600">
                      计划进度：
                      {calculatePlannedProgress(
                        project.startDate,
                        project.dueDate
                      ) ?? "—"}
                      %
                    </span>
                  </>
                )}
              </div>

              {/* 能力标签 */}
              {project.capabilities && project.capabilities.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {project.capabilities.slice(0, 4).map((cap) => (
                    <span
                      key={cap}
                      className="px-1 py-0.5 rounded text-[8px] font-mono bg-white/[0.04] text-gray-400"
                    >
                      {cap}
                    </span>
                  ))}
                </div>
              )}

              {/* 里程碑 */}
              {project.milestones && project.milestones.length > 0 && (
                <div className="space-y-0.5">
                  <div className="text-[9px] font-mono text-gray-600 uppercase tracking-wider">
                    里程碑
                  </div>
                  {project.milestones.slice(0, 3).map((m, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-1.5 text-[9px] text-gray-400"
                    >
                      {m.status === "completed" ? (
                        <CheckCircle2 size={8} className="text-emerald-400" />
                      ) : m.status === "in_progress" ? (
                        <Clock size={8} className="text-amber-400" />
                      ) : (
                        <Clock size={8} className="text-gray-600" />
                      )}
                      <span className="flex-1 truncate">{m.name}</span>
                      <span className="font-mono text-gray-600">
                        {m.dueDate}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* 进入产品链接 */}
              {project.launchUrl && (
                <button
                  onClick={() => {
                    if (/^https?:\/\//i.test(project.launchUrl!)) {
                      window.open(project.launchUrl, "_blank", "noopener,noreferrer");
                    }
                  }}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors"
                >
                  <ExternalLink size={9} />
                  进入产品
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 项目行（列表视图）
// ============================================
function ProjectRow({
  project,
  expanded,
  onToggle,
}: {
  project: Project;
  expanded: boolean;
  onToggle: () => void;
}) {
  const hMeta = HEALTH_META[project.health];
  const sMeta = STATUS_META[project.status];

  return (
    <div className="rounded-md border border-white/[0.04] bg-white/[0.02] overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-2.5 text-left hover:bg-white/[0.03] transition-colors"
      >
        {/* 健康度指示 */}
        <div
          className="w-1 h-8 rounded-full flex-shrink-0"
          style={{ background: hMeta.color }}
          title={`健康度：${hMeta.label}`}
        />

        {/* 名称 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-gray-200 font-medium truncate">
              {project.name}
            </span>
            {project.source === "runtime" && (
              <span className="px-1 rounded text-[8px] font-mono uppercase bg-cyan-500/15 text-cyan-400">
                Runtime
              </span>
            )}
          </div>
          <div className="text-[9px] font-mono text-gray-600 truncate">
            {project.description}
          </div>
        </div>

        {/* 进度 */}
        <div className="w-[80px] flex-shrink-0">
          <div className="text-[9px] font-mono text-gray-400 text-right mb-0.5">
            {project.progress}%
          </div>
          <div className="h-0.5 rounded-full bg-white/[0.04] overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${project.progress}%`,
                background: hMeta.color,
              }}
            />
          </div>
        </div>

        {/* 状态 */}
        <span
          className="px-1.5 py-0.5 rounded text-[8px] font-mono uppercase flex-shrink-0"
          style={{ background: `${sMeta.color}20`, color: sMeta.color }}
        >
          {sMeta.label}
        </span>

        {/* 负责人 */}
        <span className="flex items-center gap-0.5 text-[9px] font-mono text-gray-500 flex-shrink-0 w-[80px]">
          <User size={8} />
          {project.owner}
        </span>

        {/* 截止日 */}
        <span className="flex items-center gap-0.5 text-[9px] font-mono text-gray-500 flex-shrink-0 w-[80px]">
          <Calendar size={8} />
          {formatDate(project.dueDate)}
        </span>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-white/[0.03]"
          >
            <div className="p-3 space-y-2">
              <p className="text-[10px] text-gray-400 leading-snug">
                {project.description}
              </p>
              <div className="flex items-center gap-4 text-[9px] font-mono">
                <span className="text-gray-600">
                  健康度：
                  <span style={{ color: hMeta.color }}>{hMeta.label}</span>
                </span>
                <span className="text-gray-600">
                  来源：
                  <span className="text-gray-400">
                    {project.source === "runtime" ? "Runtime Catalog" : "本地自建"}
                  </span>
                </span>
                {project.startDate && (
                  <span className="text-gray-600">
                    开始：<span className="text-gray-400">{formatDate(project.startDate)}</span>
                  </span>
                )}
              </div>
              {project.capabilities && project.capabilities.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {project.capabilities.map((cap) => (
                    <span
                      key={cap}
                      className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-white/[0.04] text-gray-400"
                    >
                      {cap}
                    </span>
                  ))}
                </div>
              )}
              {project.milestones && project.milestones.length > 0 && (
                <div className="space-y-0.5">
                  <div className="text-[9px] font-mono text-gray-600 uppercase tracking-wider">
                    里程碑
                  </div>
                  {project.milestones.map((m, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-1.5 text-[9px] text-gray-400"
                    >
                      {m.status === "completed" ? (
                        <CheckCircle2 size={9} className="text-emerald-400" />
                      ) : m.status === "in_progress" ? (
                        <Clock size={9} className="text-amber-400" />
                      ) : (
                        <Clock size={9} className="text-gray-600" />
                      )}
                      <span className="flex-1">{m.name}</span>
                      <span className="font-mono text-gray-600">{m.dueDate}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 新增项目弹窗
// ============================================
function AddProjectModal({
  onSubmit,
  onClose,
}: {
  onSubmit: (data: {
    name: string;
    description: string;
    owner: string;
    startDate: string;
    dueDate: string;
  }) => void;
  onClose: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: "",
    description: "",
    owner: "",
    startDate: today,
    dueDate: today,
  });
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!form.name.trim()) {
      setError("请输入项目名称");
      return;
    }
    if (!form.owner.trim()) {
      setError("请输入负责人");
      return;
    }
    if (form.dueDate < form.startDate) {
      setError("截止日期不能早于开始日期");
      return;
    }
    onSubmit(form);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md flex flex-col rounded-xl border border-purple-500/30 bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
              <Plus size={13} className="text-purple-400" />
            </div>
            <h3 className="text-[13px] font-semibold text-white">新增项目</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 表单 */}
        <div className="flex-1 p-5 space-y-3">
          {error && (
            <div className="px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-[10px] text-red-300 font-mono">
              {error}
            </div>
          )}

          <FormField label="项目名称">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="输入项目名称"
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-purple-500/30"
            />
          </FormField>

          <FormField label="项目描述">
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="简要描述项目目标"
              rows={2}
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-purple-500/30 resize-none"
            />
          </FormField>

          <FormField label="负责人">
            <input
              type="text"
              value={form.owner}
              onChange={(e) => setForm({ ...form, owner: e.target.value })}
              placeholder="负责人姓名"
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-purple-500/30"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="开始日期">
              <input
                type="date"
                value={form.startDate}
                onChange={(e) =>
                  setForm({ ...form, startDate: e.target.value })
                }
                className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none focus:border-purple-500/30"
              />
            </FormField>
            <FormField label="截止日期">
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none focus:border-purple-500/30"
              />
            </FormField>
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06] bg-white/[0.01]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-white/[0.06] text-[10px] font-mono text-gray-400 hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-purple-500/20 border border-purple-500/30 text-[10px] font-mono text-purple-300 hover:bg-purple-500/30 transition-colors"
          >
            <TrendingUp size={10} />
            创建项目
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 表单字段包装
// ============================================
function FormField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
